import gql from 'graphql-tag';

import {
    alternateEntryBalance,
    clientOrder,
    confirmFragment,
    creditBalance,
    lectureInstance,
    lectureTerm,
    lectureType,
    officeEmployee,
    officePlace,
    passBalance,
    user,
    userReservations,
    workshop,
} from '../fragments';

export const createOfficeLectureInstance = gql`
    mutation CreateOfficeLectureInstance($input: CreateOfficeLectureInstanceInput!) {
        createOfficeLectureInstance(input: $input) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const createOfficeCourse = gql`
    mutation CreateOfficeCourse($input: CreateOfficeLectureCourseInput!) {
        createOfficeCourse(input: $input) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const createOfficeWorkshop = gql`
    mutation CreateOfficeWorkshop($input: CreateOfficeWorkshopInput!) {
        createOfficeWorkshop(input: $input) {
            ...WorkshopFragment
        }
    }
    ${workshop}
`;

export const updateOfficeWorkshop = gql`
    mutation UpdateOfficeWorkshop($input: UpdateOfficeWorkshopInput!) {
        updateOfficeWorkshop(input: $input) {
            ...WorkshopFragment
        }
    }
    ${workshop}
`;

export const updateOfficeLectureTermById = gql`
    mutation UpdateOfficeLectureTermById($lectureTermId: Int!, $input: UpdateOfficeLectureTermInput!) {
        updateOfficeLectureTermById(lectureTermId: $lectureTermId, input: $input) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const updateOfficeWorkshopVisibilityById = gql`
    mutation UpdateOfficeWorkshopVisibilityById(
        $officeId: Int!
        $workshopId: Int!
        $isVisibleInPublicCalendar: Boolean!
    ) {
        updateOfficeWorkshopVisibilityById(
            officeId: $officeId
            workshopId: $workshopId
            isVisibleInPublicCalendar: $isVisibleInPublicCalendar
        ) {
            ...WorkshopFragment
        }
    }
    ${workshop}
`;

export const updateOfficeCanceledLectureTermVisibilityById = gql`
    mutation UpdateOfficeCanceledLectureTermVisibilityById(
        $officeId: Int!
        $lectureTermId: Int!
        $isVisibleInPublicCalendar: Boolean!
    ) {
        updateOfficeCanceledLectureTermVisibilityById(
            officeId: $officeId
            lectureTermId: $lectureTermId
            isVisibleInPublicCalendar: $isVisibleInPublicCalendar
        ) {
            ...LectureTermFragment
        }
    }
    ${lectureTerm}
`;

export const addSingleCourseTerm = gql`
    mutation AddSingleCourseTerm($lectureInstanceId: Int!, $input: AddCourseTermInput!) {
        addSingleCourseTerm(lectureInstanceId: $lectureInstanceId, input: $input) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const updateCourseAndItsTermsById = gql`
    mutation UpdateCourseAndItsTermsById($lectureTermId: Int!, $input: UpdateCourseInput!) {
        updateCourseAndItsTermsById(lectureTermId: $lectureTermId, input: $input) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const cancelLectureAttendeeReservationById = gql`
    mutation CancelLectureAttendeeReservationById(
        $officeId: Int!
        $organisationId: Int!
        $reservationId: Int!
        $cancelStrategyChoice: String!
        $description: String
        $cancelSingleCourseTermForCourseAttendee: Boolean!
    ) {
        cancelLectureAttendeeReservationById(
            officeId: $officeId
            organisationId: $organisationId
            reservationId: $reservationId
            cancelStrategyChoice: $cancelStrategyChoice
            description: $description
            cancelSingleCourseTermForCourseAttendee: $cancelSingleCourseTermForCourseAttendee
        ) {
            ...UserReservationFragment
            user {
                id
                userId
                name
                email
                phone
                image
            }
        }
    }
    ${userReservations}
`;

export const cancelClientOrderByOffice = gql`
    mutation CancelClientOrderByOffice(
        $officeId: Int!
        $organisationId: Int!
        $clientOrderId: Int!
        $cancelStrategyChoice: String!
        $description: String
        $percent: Int!
    ) {
        cancelClientOrderByOffice(
            officeId: $officeId
            organisationId: $organisationId
            clientOrderId: $clientOrderId
            cancelStrategyChoice: $cancelStrategyChoice
            description: $description
            percent: $percent
        ) {
            ...ClientOrderFragment
        }
    }
    ${clientOrder}
`;

export const cancelMyLectureTermReservationById = gql`
    mutation CancelMyLectureTermReservationById($userId: Int!, $reservationId: Int!) {
        cancelMyLectureTermReservationById(userId: $userId, reservationId: $reservationId) {
            ...UserReservationFragment
            user {
                id
                name
                email
                phone
                image
            }
        }
    }
    ${userReservations}
`;

export const cancelMyCourseSessionReservationById = gql`
    mutation CancelMyCourseSessionReservationById($userId: Int!, $reservationId: Int!) {
        cancelMyCourseSessionReservationById(userId: $userId, reservationId: $reservationId) {
            reservations {
                ...UserReservationFragment
                user {
                    id
                    name
                    email
                    phone
                    image
                }
            }
            creditBalance {
                ...CreditBalanceFragment
            }
            passBalance {
                ...PassBalanceFragment
            }
            alternateEntry {
                ...AlternateEntryBalanceFragment
            }
        }
    }
    ${userReservations}
    ${creditBalance}
    ${passBalance}
    ${alternateEntryBalance}
`;

export const cancelMyLectureTermReservationByPublicCode = gql`
    mutation CancelMyLectureTermReservationByPublicCode($reservationToken: String!) {
        cancelMyLectureTermReservationByPublicCode(reservationToken: $reservationToken) {
            ...UserReservationFragment
            user {
                id
                userId
                name
                email
                phone
                image
            }
        }
    }
    ${userReservations}
`;

export const updateReservationById = gql`
    mutation UpdateReservationById($officeId: Int!, $reservationId: Int!, $input: UpdateReservationInput!) {
        updateReservationById(officeId: $officeId, reservationId: $reservationId, input: $input) {
            ...UserReservationFragment
        }
    }
    ${userReservations}
`;

export const updateAttendeeIsPresentAndPaymentIsCompleteByReservationId = gql`
    mutation UpdateAttendeeIsPresentAndPaymentIsCompleteByReservationId(
        $officeId: Int!
        $reservationId: Int!
        $isPresent: Boolean!
    ) {
        updateAttendeeIsPresentAndPaymentIsCompleteByReservationId(
            officeId: $officeId
            reservationId: $reservationId
            isPresent: $isPresent
        ) {
            ...UserReservationFragment
        }
    }
    ${userReservations}
`;

export const updateAttendeeNotPresentAndPaymentIsNotPaidByReservationId = gql`
    mutation UpdateAttendeeNotPresentAndPaymentIsNotPaidByReservationId($officeId: Int!, $reservationId: Int!) {
        updateAttendeeNotPresentAndPaymentIsNotPaidByReservationId(officeId: $officeId, reservationId: $reservationId) {
            ...UserReservationFragment
        }
    }
    ${userReservations}
`;

export const sendSummaryMessage = gql`
    mutation SendSummaryMessage($input: SendSummaryMessageInput!) {
        sendSummaryMessage(input: $input) {
            success
        }
    }
`;

export const sendMessageToClient = gql`
    mutation SendMessageToClient($officeId: Int!, $email: String!, $message: String!) {
        sendMessageToClient(officeId: $officeId, email: $email, message: $message) {
            success
        }
    }
`;

export const updateLectureType = gql`
    mutation UpdateLectureType($input: LectureTypeInput!) {
        updateLectureType(input: $input) {
            ...LectureTypeFragment
        }
    }
    ${lectureType}
`;

export const deleteOfficeLectureType = gql`
    mutation DeleteOfficeLectureType($input: DeleteOfficeLectureTypeInput!) {
        deleteOfficeLectureType(input: $input) {
            ...LectureTypeFragment
        }
    }
    ${lectureType}
`;

export const publishDraftByLectureId = gql`
    mutation PublishDraftByLectureId($lectureId: Int!, $officeId: Int!) {
        publishDraftByLectureId(lectureId: $lectureId, officeId: $officeId) {
            ...LectureInstanceFragment
        }
    }
    ${lectureInstance}
`;

export const deleteDraftByLectureId = gql`
    mutation DeleteDraftByLectureId($lectureId: Int!, $organisationId: Int!, $officeId: Int!) {
        deleteDraftByLectureId(lectureId: $lectureId, organisationId: $organisationId, officeId: $officeId) {
            confirm {
                ...ConfirmFragment
            }
            deletedTerms {
                graphQlId
                start
            }
        }
    }
    ${confirmFragment}
`;

export const cancelCourseTermById = gql`
    mutation CancelCourseTermById(
        $lectureTermId: Int!
        $organisationId: Int!
        $officeId: Int!
        $emailText: String!
        $smsText: String
        $organisationEmployeeId: Int!
        $isVisibleInPublicCalendar: Boolean!
    ) {
        cancelCourseTermById(
            lectureTermId: $lectureTermId
            organisationId: $organisationId
            officeId: $officeId
            emailText: $emailText
            smsText: $smsText
            organisationEmployeeId: $organisationEmployeeId
            isVisibleInPublicCalendar: $isVisibleInPublicCalendar
        ) {
            ...LectureTermFragment
            lectureInstance {
                ...LectureInstanceFragment
                place {
                    ...OfficePlaceFragment
                }
                lecturer {
                    ...OfficeEmployeeFragment
                }
            }
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const cancelLectureTermById = gql`
    mutation CancelLectureTermById($lectureTermId: Int!, $organisationId: Int!, $input: CancelLectureTermInput!) {
        cancelLectureTermById(lectureTermId: $lectureTermId, organisationId: $organisationId, input: $input) {
            lectureTerm {
                ...LectureTermFragment
                lectureInstance {
                    ...LectureInstanceFragment
                    place {
                        ...OfficePlaceFragment
                    }
                    lecturer {
                        ...OfficeEmployeeFragment
                    }
                }
            }
            operation
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const cancelWorkshopById = gql`
    mutation CancelWorkshopById(
        $workshopId: Int!
        $organisationId: Int!
        $officeId: Int!
        $emailText: String!
        $smsText: String
        $organisationEmployeeId: Int!
    ) {
        cancelWorkshopById(
            workshopId: $workshopId
            organisationId: $organisationId
            officeId: $officeId
            emailText: $emailText
            smsText: $smsText
            organisationEmployeeId: $organisationEmployeeId
        ) {
            ...WorkshopFragment
        }
    }
    ${workshop}
`;

export const cancelRepeatedLectureTermsById = gql`
    mutation CancelRepeatedLectureTermsById($lectureTermIds: [Int]!, $organisationId: Int!, $officeId: Int!) {
        cancelRepeatedLectureTermsById(
            lectureTermIds: $lectureTermIds
            organisationId: $organisationId
            officeId: $officeId
        ) {
            lectureTerm {
                ...LectureTermFragment
                lectureInstance {
                    ...LectureInstanceFragment
                    place {
                        ...OfficePlaceFragment
                    }
                    lecturer {
                        ...OfficeEmployeeFragment
                    }
                }
            }
            operation
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;

export const cancelCourseById = gql`
    mutation CancelCourseById(
        $courseId: Int!
        $organisationId: Int!
        $officeId: Int!
        $isVisibleInPublicCalendar: Boolean!
    ) {
        cancelCourseById(
            courseId: $courseId
            organisationId: $organisationId
            officeId: $officeId
            isVisibleInPublicCalendar: $isVisibleInPublicCalendar
        ) {
            lectureTerm {
                ...LectureTermFragment
                lectureInstance {
                    ...LectureInstanceFragment
                    place {
                        ...OfficePlaceFragment
                    }
                    lecturer {
                        ...OfficeEmployeeFragment
                    }
                }
            }
            operation
        }
    }
    ${lectureTerm}
    ${lectureInstance}
    ${officePlace}
    ${officeEmployee}
`;
